import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@dreamtree-org/twreact-ui';
import { CalendarClock, History, PackageCheck, Pencil, Trash2 } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import PageHeader from '@/components/PageHeader';
import ProductImage from '@/components/ProductImage';
import ExpiryBadge from '@/components/ExpiryBadge';
import ExpiryEditor from '@/components/ExpiryEditor';
import RestockDialog from '@/components/RestockDialog';
import ConfirmDialog from '@/components/ConfirmDialog';
import { ErrorState, LoadingState } from '@/components/States';
import { useAppToast } from '@/lib/toast';
import { currency, date, errorMessage, label, quantity } from '@/lib/format';
import { DEFAULT_POLICY, expiryMeta } from '@/lib/expiry';
import Panel from '@/components/Panel';

/** One labelled fact. */
function Fact({ label: title, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--app-muted)]">{title}</div>
      <div className="mt-0.5 text-sm font-medium text-[var(--app-text)]">{children}</div>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const toast = useAppToast();
  const { fetchApi } = useAppApi();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(false);
  const [restocking, setRestocking] = useState(false);
  const [history, setHistory] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetchApi('productDetail', id);
      setProduct((res && res.data) || null);

      // The batch history is supplementary. Fetched separately and allowed to
      // fail on its own: a product's details must still render when the history
      // call is slow, forbidden, or returns something unexpected — losing the
      // whole page over a side panel is a worse outcome than losing the panel.
      try {
        const past = await fetchApi('productHistory', id);
        setHistory(Array.isArray(past && past.data) ? past.data : []);
      } catch {
        setHistory([]);
      }
    } catch (ex) {
      setErr(errorMessage(ex, 'Could not load this product'));
    } finally {
      setLoading(false);
    }
    // fetchApi identity changes each render in the library hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async () => {
    setDeleting(true);
    try {
      await fetchApi('crud', { model: 'Product', action: 'delete', where: { id } });
      toast.success('Product deleted', product.name);
      nav('/products');
    } catch (ex) {
      toast.error('Could not delete', errorMessage(ex));
      setDeleting(false);
    }
  };

  if (loading) return <LoadingState rows={6} />;
  if (err || !product) return <ErrorState message={err || 'That product no longer exists'} onRetry={load} />;

  const meta = expiryMeta(product.expiry_state);
  const shelfLife =
    product.manufactured_date && product.expiry_date
      ? Math.round(
          (new Date(product.expiry_date).getTime() - new Date(product.manufactured_date).getTime()) / 86400000
        )
      : null;

  return (
    <div className="space-y-5">
      <PageHeader
        title={product.name}
        subtitle={`${product.sku}${product.brand ? ` · ${product.brand}` : ''}`}
        trail={[{ label: 'Products', to: '/products' }, { label: product.name }]}
        actions={
          <div className="flex flex-wrap gap-2">
            <Can module="inventory" action="update">
              <Button variant="outline" leftIcon={<CalendarClock size={16} />} onClick={() => setEditing(true)}>
                Update expiry
              </Button>
              <Button variant="outline" leftIcon={<PackageCheck size={16} />} onClick={() => setRestocking(true)}>
                {product.status === 'disposed' ? 'Record delivery' : 'Stock out & reorder'}
              </Button>
              <Button leftIcon={<Pencil size={16} />} onClick={() => nav(`/products/${id}/edit`)}>
                Edit
              </Button>
            </Can>
            <Can module="inventory" action="delete">
              <Button variant="ghost" leftIcon={<Trash2 size={16} />} onClick={() => setConfirm(true)}>
                Delete
              </Button>
            </Can>
          </div>
        }
      />

      {/* The expiry state is the headline, not a field in a list — it is the
          reason this record is being looked at. */}
      <Panel
        className="flex flex-col gap-4 border p-4 sm:flex-row sm:items-center sm:p-5"
        style={{ background: meta.bg, borderColor: meta.border }}
      >
        <ProductImage src={product.image_url} name={product.name} className="h-32 w-32 shrink-0 sm:h-36 sm:w-36" />
        <div className="min-w-0 flex-1 space-y-2">
          <ExpiryBadge state={product.expiry_state} days={product.days_to_expiry} variant="full" size="lg" />
          <p className="text-sm" style={{ color: meta.fg }}>
            {meta.blurb}
          </p>
          <div className="nums text-sm text-[var(--app-muted)]">
            Expiry date: <span className="font-semibold text-[var(--app-text)]">{date(product.expiry_date)}</span>
            {product.batch_no ? ` · Batch ${product.batch_no}` : ''}
          </div>
        </div>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel className="space-y-4 p-4 sm:p-5 lg:col-span-2">
          <h2 className="text-sm font-bold uppercase tracking-wide text-[var(--app-muted)]">Details</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <Fact label="Category">{product.category || '—'}</Fact>
            <Fact label="Brand">{product.brand || '—'}</Fact>
            <Fact label="Status">{label(product.status)}</Fact>
            <Fact label="Quantity">
              <span className="nums">{quantity(product.quantity, product.unit)}</span>
            </Fact>
            <Fact label="Unit price">
              <span className="nums">{currency(product.unit_price, { exact: true })}</span>
            </Fact>
            <Fact label="Stock value">
              <span className="nums">
                {currency(Number(product.quantity || 0) * Number(product.unit_price || 0))}
              </span>
            </Fact>
            <Fact label="Manufactured">
              <span className="nums">{date(product.manufactured_date)}</span>
            </Fact>
            <Fact label="Expires">
              <span className="nums">{date(product.expiry_date)}</span>
            </Fact>
            <Fact label="Shelf life">
              <span className="nums">{shelfLife != null ? `${shelfLife} days` : '—'}</span>
            </Fact>
          </div>
          {product.notes ? (
            <div className="rounded-xl bg-[var(--app-surface-2)] p-3 text-sm text-[var(--app-text)]">
              {product.notes}
            </div>
          ) : null}
        </Panel>

        <Panel className="space-y-4 p-4 sm:p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-[var(--app-muted)]">Stored in</h2>
          {product.warehouse ? (
            <div className="space-y-3">
              <Fact label="Warehouse">{product.warehouse.name}</Fact>
              <Fact label="Location">
                {[product.warehouse.city, product.warehouse.state].filter(Boolean).join(', ') || '—'}
              </Fact>
              <Fact label="Manager">{product.warehouse.manager_name || '—'}</Fact>
              <Fact label="Expiry policy">
                <span className="nums">
                  Critical within {product.warehouse.critical_days}d · warning within {product.warehouse.warning_days}d
                </span>
              </Fact>
              <Button variant="outline" size="sm" fullWidth onClick={() => nav('/warehouse')}>
                Warehouse settings
              </Button>
            </div>
          ) : (
            <p className="text-sm text-[var(--app-muted)]">This product is not assigned to a warehouse.</p>
          )}
          <div className="space-y-1 border-t border-[var(--app-border)] pt-3 text-xs text-[var(--app-muted)]">
            <div>Added {date(product.created_at)}</div>
            <div>Last updated {date(product.updated_at)}</div>
          </div>
        </Panel>
      </div>

      {/* Every batch that has passed through this row. The expiry date on the
          card above is only the CURRENT one; this is how the shelf has actually
          turned over, and whether it turned over in time. */}
      <Panel className="p-4 sm:p-5">
        <div className="mb-4 flex items-center gap-2">
          <History size={16} className="text-[var(--app-muted)]" />
          <h2 className="text-sm font-bold uppercase tracking-wide text-[var(--app-muted)]">Batch history</h2>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-[var(--app-muted)]">
            No batch has been stocked out yet. When this one is rotated, the outgoing batch is recorded here.
          </p>
        ) : (
          <div className="space-y-2">
            {history.map((b) => (
              <div
                key={b.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--app-border)] p-3"
                style={{ borderLeft: `4px solid ${b.wasted ? 'var(--exp-expired-solid)' : 'var(--exp-fresh-solid)'}` }}
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[var(--app-text)]">
                    {b.batch_no || 'No batch number'}{' '}
                    <span className="font-normal text-[var(--app-muted)]">· {label(b.reason)}</span>
                  </div>
                  <div className="nums text-xs text-[var(--app-muted)]">
                    Expiry {date(b.expiry_date)} · stocked out {date(b.closed_at)}
                    {b.closed_by_name ? ` by ${b.closed_by_name}` : ''}
                  </div>
                  {b.notes ? <div className="mt-1 text-xs text-[var(--app-text)]">{b.notes}</div> : null}
                </div>
                <div className="text-right">
                  <div className="nums text-sm font-semibold text-[var(--app-text)]">
                    {quantity(b.quantity, b.unit)} · {currency(b.value)}
                  </div>
                  {/* The number that matters: pulled early, or left too long. */}
                  <div
                    className="nums text-xs font-medium"
                    style={{ color: b.days_to_expiry_at_close < 0 ? 'var(--exp-expired-fg)' : 'var(--exp-fresh-fg)' }}
                  >
                    {b.days_to_expiry_at_close == null
                      ? 'No expiry recorded'
                      : b.days_to_expiry_at_close < 0
                        ? `${Math.abs(b.days_to_expiry_at_close)} days past date`
                        : `pulled ${b.days_to_expiry_at_close} days early`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <RestockDialog
        product={product}
        policy={
          product.warehouse
            ? { criticalDays: product.warehouse.critical_days, warningDays: product.warehouse.warning_days }
            : DEFAULT_POLICY
        }
        isOpen={restocking}
        onClose={() => setRestocking(false)}
        onSaved={load}
      />

      <ExpiryEditor
        product={product}
        policy={
          product.warehouse
            ? { criticalDays: product.warehouse.critical_days, warningDays: product.warehouse.warning_days }
            : DEFAULT_POLICY
        }
        isOpen={editing}
        onClose={() => setEditing(false)}
        onSaved={(saved) => setProduct((p) => ({ ...p, ...(saved || {}) }))}
      />

      <ConfirmDialog
        isOpen={confirm}
        title="Delete this product?"
        confirmLabel="Delete product"
        loading={deleting}
        onConfirm={remove}
        onClose={() => setConfirm(false)}
        message={`"${product.name}" (${product.sku}) and its photo will be removed. This cannot be undone.`}
      />
    </div>
  );
}
